import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import BrandMark from "@/components/BrandMark";

export default function PagoExito() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    let timer = null;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/payments/stripe/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts >= 8) {
          setStatus("failed");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch {
        if (attempts >= 8) {
          setStatus("failed");
        } else {
          timer = setTimeout(poll, 2000);
        }
      }
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center px-5">
      <div className="max-w-md w-full text-center">
        <div className="mb-6 flex justify-center">
          <BrandMark size="md" />
        </div>

        {status === "checking" && (
          <>
            <Loader2 className="w-16 h-16 text-zinc-500 mx-auto animate-spin mb-4" />
            <h1 className="text-2xl font-black text-zinc-900 mb-2">Verificando tu pago…</h1>
            <p className="text-sm text-zinc-600">No cierres esta ventana. Tardamos unos segundos.</p>
          </>
        )}

        {status === "paid" && (
          <div className="bg-white border-2 border-emerald-300 rounded-3xl p-8 shadow-lg">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-12 h-12 text-emerald-600" strokeWidth={2.5} />
            </div>
            <div className="inline-flex items-center gap-1 bg-emerald-100 px-3 py-1 rounded-full mb-3">
              <Sparkles className="w-3 h-3 text-emerald-700" />
              <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Plan activado</span>
            </div>
            <h1 className="text-2xl font-black text-zinc-900 mb-2">¡Pago exitoso!</h1>
            <p className="text-sm text-zinc-700 leading-relaxed mb-6">
              Tu plan ya está activo. Ahora tienes llamadas disponibles con el asistente de voz.
            </p>
            <Button
              data-testid="pago-exito-continue"
              onClick={() => navigate("/dashboard")}
              className="w-full h-14 font-black bg-zinc-900 hover:bg-zinc-800 text-white rounded-xl"
            >
              Ir a mi cuenta
            </Button>
          </div>
        )}

        {(status === "failed" || status === "expired") && (
          <div className="bg-white border-2 border-zinc-200 rounded-3xl p-8">
            <XCircle className="w-16 h-16 text-zinc-400 mx-auto mb-4" />
            <h1 className="text-2xl font-black text-zinc-900 mb-2">
              {status === "expired" ? "Sesión expirada" : "No pudimos verificar el pago"}
            </h1>
            <p className="text-sm text-zinc-600 mb-6">
              Si crees que el pago se realizó, contáctanos. De lo contrario, intenta de nuevo.
            </p>
            <Button onClick={() => navigate("/precios")} className="w-full h-14 font-black bg-zinc-900 text-white rounded-xl">
              Volver a precios
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
